import { createTheme, PaletteMode } from '@mui/material'
import { grey } from '@mui/material/colors'
import DraggableDialog from '../components/common/DraggableDialog'
import ThinArrowDownIcon from '../assets/icons/ThinArrowDownIcon'
import ThinArrowLeftIcon from '../assets/icons/ThinArrowLeftIcon'
import ThinArrowRightIcon from '../assets/icons/ThinArrowRightIcon'
import { blue, red } from '../assets/colors'

export const darkHoverBtnColor = grey[800]
export const lightHoverBtnColor = grey[200]
export const darkBackgroundSecondaryTextColor = 'rgba(190, 190, 190, 1)'

declare module '@mui/material/styles' {
    interface TypeText {
        tertiary: string
    }
    interface TypeTextOptions {
        tertiary?: string
    }
    interface Palette {
        call: Palette['primary']
        put: Palette['primary']
        buy: Palette['primary']
        sell: Palette['primary']
    }
    interface PaletteOptions {
        put?: PaletteOptions['primary']
        call?: PaletteOptions['primary']
        buy?: PaletteOptions['primary']
        sell?: PaletteOptions['primary']
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        call: true
        put: true
        buy: true
        sell: true
    }
}

let theme = createTheme({
    // Theme customization goes here as usual, including tonalOffset and/or
    // contrastThreshold as the augmentColor() function relies on these
})
export const getDesignTokens = (mode: PaletteMode) => {
    const darkMode = mode === 'dark'
    const lightShadows = [
        'none',
        'none',
        '0px 1px 2px rgba(0,0,0,0.15)',
        '0px 1px 3px rgba(0,0,0,0.16)',
        '0px 2px 4px rgba(0,0,0,0.17)',
        '0px 2px 6px rgba(0,0,0,0.18)',
        '0px 3px 8px rgba(0,0,0,0.19)',
        '0px 3px 10px rgba(0,0,0,0.20)',
        '0px 4px 12px rgba(0,0,0,0.21)',
        '0px 4px 14px rgba(0,0,0,0.22)',
        '0px 5px 16px rgba(0,0,0,0.23)',
        '0px 5px 18px rgba(0,0,0,0.24)',
        '0px 6px 20px rgba(0,0,0,0.25)',
        '0px 6px 22px rgba(0,0,0,0.26)',
        '0px 7px 24px rgba(0,0,0,0.27)',
        '0px 7px 26px rgba(0,0,0,0.28)',
        '0px 8px 28px rgba(0,0,0,0.29)',
        '0px 8px 30px rgba(0,0,0,0.30)',
        '0px 9px 32px rgba(0,0,0,0.31)',
        '0px 9px 34px rgba(0,0,0,0.32)',
        '0px 10px 36px rgba(0,0,0,0.33)',
        '0px 10px 38px rgba(0,0,0,0.34)',
        '0px 11px 40px rgba(0,0,0,0.35)',
        '0px 11px 42px rgba(0,0,0,0.36)',
        '0px 12px 44px rgba(0,0,0,0.37)',
        '0px 12px 46px rgba(0,0,0,0.38)'
    ]
    return {
        shadows: lightShadows,
        zIndex: {
            appBar: 1100, // top appbar
            drawer: 1200, // side drawers
            tooltip: 1500, // tooltips
            modal: 12000 // dialogs, modals
        },
        palette: {
            mode,
            call: theme.palette.augmentColor({
                color: {
                    main: darkMode ? '#7B9EFF' : '#2F2FCD'
                },
                name: 'call'
            }),
            put: theme.palette.augmentColor({
                color: {
                    main: '#FFA719'
                },
                name: 'put'
            }),
            buy: theme.palette.augmentColor({
                color: {
                    main: blue
                },
                name: 'buy'
            }),
            sell: theme.palette.augmentColor({
                color: {
                    main: red
                },
                name: 'sell'
            }),
            ...(mode === 'light'
                ? {
                      divider: '#eeeeee',
                      background: {
                          default: '#f6f6f6',
                          paper: '#ffffff'
                      },
                      primary: {
                          main: '#333333',
                          light: '#888888',
                          dark: '#000000',
                          contrastText: '#ffffff'
                      },
                      text: {
                          disabled: 'rgba(31, 31, 31, 0.38)',
                          primary: 'rgb(31,31, 31)',
                          secondary: 'rgba(31, 31, 31, 0.6)',
                          tertiary: 'rgba(31, 31, 31, 0.45)',
                      },
                      customValues: {
                          divider: '#eeeeee'
                      }
                  }
                : {
                      divider: '#202020',
                      background: {
                          default: '#181818',
                          paper: '#080808'
                      },
                      primary: {
                          main: '#eee',
                          light: '#ccc',
                          dark: '#fff',
                          contrastText: '#0a0a0a'
                      },
                      text: {
                          disabled: 'rgba(255, 255, 255, 0.5)',
                          icon: 'rgba(255, 255, 255, 0.5)',
                          primary: '#dbdbdb',
                          secondary: 'rgba(255, 255, 255, 0.7)',
                          tertiary: 'rgba(255, 255, 255, 0.5)',
                      },
                      customValues: {
                          divider: '#2b2e38'
                      }
                  })
        },
        components: {
            MuiMenu: {
                defaultProps: {
                    elevation: 3,
                    disableScrollLock: true,
                    disableEnforceFocus: true,
                    disableAutoFocus: true,
                    disableRestoreFocus: true
                },
                styleOverrides: {
                    paper: {
                        transition: 'none !important'
                    }
                }
            },
            MuiButtonBase: {
                defaultProps: { disableRipple: true },
                styleOverrides: {
                    root: {
                        cursor: 'default'
                    }
                }
            },
            MuiListItemButton: { defaultProps: { disableRipple: true } },
            MuiListItem: {
                defaultProps: { dense: true }
            },
            MuiMenuItem: {
                defaultProps: { dense: true, disableRipple: true },
                styleOverrides: {
                    root: {
                        paddingLeft: '8px',
                        paddingRight: '8px'
                    }
                }
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                        transition: 'none !important'
                    }
                }
            },
            MuiDialog: {
                defaultProps: {
                    PaperComponent: DraggableDialog
                },
                styleOverrides: {
                    container: {
                        transition: 'none !important'
                    },
                    paper: {
                        backgroundColor: darkMode ? '#101010' : '#ffffff',
                        backgroundImage: 'none',
                        ...(darkMode
                            ? {
                                  border: '1px solid',
                                  borderColor: '#2b2e38',
                                  boxSizing: 'border-box'
                              }
                            : {})
                    },
                    root: {
                        '& .MuiButton-root': {
                            height: '34px'
                        }
                    }
                }
            },
            MuiCheckbox: {
                defaultProps: {
                    disableRipple: true
                },
                styleOverrides: {
                    root: {
                        padding: '4px'
                    }
                }
            },

            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        padding: '16px' // small padding globally
                    }
                }
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        padding: '0 16px 16px 16px' // small padding globally
                    }
                }
            },

            MuiDateTimePicker: {
                defaultProps: {
                    slots: {
                        switchViewIcon: ThinArrowDownIcon,
                        leftArrowIcon: ThinArrowLeftIcon,
                        rightArrowIcon: ThinArrowRightIcon
                    },
                    slotProps: {
                        actionBar: {
                            sx: {
                                padding: 1 // footer padding
                            }
                        }
                    }
                }
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: '16px 16px'
                    }
                }
            },
            MuiPickersPopper: {
                styleOverrides: {
                    root: {
                        transition: 'none !important'
                    },
                    paper: {
                        transition: 'none !important'
                    }
                }
            },
            MuiModal: {
                styleOverrides: {
                    root: {
                        transition: 'none !important'
                    }
                }
            },
            MuiPopover: {
                defaultProps: {
                    disableScrollLock: true,
                    disableEnforceFocus: true,
                    disableAutoFocus: true,
                    disableRestoreFocus: true
                },
                styleOverrides: {
                    root: {
                        transition: 'none !important'
                    },
                    paper: {
                        transition: 'none !important',
                        backgroundColor: darkMode ? '#101010' : '#ffffff',
                        backgroundImage: 'unset',
                        ...(darkMode && {
                            border: '1px solid rgba(255, 255, 255, 0.04)'
                        })
                    }
                }
            },
            MuiSkeleton: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === 'light' ? '#00000008' : '#ffffff08'
                    }
                }
            },
            MuiSelect: {
                defaultProps: {
                    size: 'small',
                    IconComponent: ThinArrowDownIcon
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === 'light' ? '#ffffff' : '#080808',
                        backgroundImage: 'none'
                    }
                }
            },
            MuiInputBase: {
                defaultProps: {
                    size: 'small'
                },
                styleOverrides: {
                    root: ({ ownerState }: any) =>
                        ownerState.size === 'small'
                            ? {
                                  fontSize: '14px'
                              }
                            : {}
                }
            },
            MuiInputLabel: {
                defaultProps: {
                    size: 'small',
                    shrink: true
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        '&:not(.MuiInputLabel-shrink)': {
                            height: '34px'
                        },
                        '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
                            height: 'auto',
                            px: 0.75,
                            zIndex: 1,
                            backgroundColor: theme.palette.background.paper,
                            transform: 'translate(14px, -9px) scale(0.75)'
                        },
                        '& input:-webkit-autofill': {
                            WebkitBoxShadow: `0 0 0 1000px ${darkMode ? '#080b16' : '#eeeeee'} inset`,
                            WebkitTextFillColor: darkMode ? '#fff' : 'rgb(31,31, 31)',
                            transition: 'background-color 5000s ease-in-out 0s',
                            paddingTop: 5,
                            paddingBottom: 5
                        }
                    })
                }
            },
            MuiList: {
                styleOverrides: {
                    height: '34px'
                }
            },
            MuiIconButton: {
                defaultProps: {
                    disableRipple: true
                },
                styleOverrides: {
                    root: {
                        cursor: 'default',
                        '&:hover': {
                            background: 'none'
                        }
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableRipple: true
                },
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: 4,
                        cursor: 'default'
                    },
                    outlined: ({ ownerState }) => ({
                        ...(ownerState.color === 'default' || ownerState.color === 'inherit' || !ownerState.color
                            ? {
                                  borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgb(230, 230, 230)'
                              }
                            : {})
                    })
                }
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: 4,
                        cursor: 'default'
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        '&.MuiChip-clickable': {
                            cursor: 'default'
                        }
                    }
                }
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        '&.MuiDataGrid-withBorderColor, & .MuiDataGrid-withBorderColor': {
                            borderColor: darkMode ? 'rgba(81, 81, 81, 0.25)' : 'rgba(224, 224, 224, 0.25)'
                        }
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    notchedOutline: ({ theme }) => ({
                        borderRadius: 4,
                        // Unlabeled outlined inputs render a .notranslate ZWSP in the
                        // legend. Do not pad/expand that legend — it punches a gap in
                        // the top border. Only style real floating-label notches.
                        '& legend span:not(.notranslate)': {
                            paddingLeft: theme.spacing(0.75),
                            paddingRight: theme.spacing(0.8)
                        }
                    })
                }
            },
            MuiTooltip: {
                styleOverrides: {
                    popper: {
                        zIndex: 999999
                    },
                    tooltip: ({ theme }) => ({
                        '& kbd': {
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '22px',
                            width: '22px',
                            height: '22px',
                            padding: 0,
                            fontSize: '12px',
                            lineHeight: 1,
                            borderRadius: '4px',
                            margin: '0 2px',
                            fontFamily: 'monospace',
                            boxSizing: 'border-box',
                            verticalAlign: 'middle',
                            ...(theme.palette.mode === 'dark'
                                ? {
                                      backgroundColor: theme.palette.background.paper,
                                      color: theme.palette.text.primary,
                                      boxShadow: '0 2px 0 rgba(255, 255, 255, 0.22)'
                                  }
                                : {
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                      color: theme.palette.common.white,
                                      boxShadow: '0 2px 0 rgba(255, 255, 255, 0.35)'
                                  })
                        },
                        ...(theme.palette.mode === 'dark' && {
                            backgroundColor: theme.palette.background.default,
                            color: theme.palette.text.primary
                        })
                    }),
                    arrow: ({ theme }) => ({
                        ...(theme.palette.mode === 'dark' && {
                            color: theme.palette.background.default
                        })
                    })
                },
                defaultProps: {
                    enterDelay: 800,
                    enterNextDelay: 300,
                    leaveDelay: 100
                }
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        minHeight: 32 // reduce height of Tabs container
                    },
                    indicator: {
                        height: 2 // optional: customize indicator thickness
                    }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        minHeight: 32, // reduce height of each tab
                        paddingTop: 4,
                        paddingBottom: 4,
                        paddingLeft: 0,
                        paddingRight: 0,
                        minWidth: 60,
                        marginRight: 8,
                        textTransform: 'none'
                    }
                }
            }
        }
    }
}

declare module '@mui/material/styles' {
    interface Palette {
        call: Palette['call']
    }

    interface PaletteOptions {
        call?: PaletteOptions['primary']
    }
}
